/**
 * Managed AI customer API client.
 *
 * Provides tenant-scoped client helpers for:
 * - GET /v1/managed-ai/funding: tenant funding preference and availability
 * - PUT /v1/managed-ai/funding: set tenant funding preference
 * - GET /v1/managed-ai/balance: tenant-scoped credit balance projection
 * - GET /v1/managed-ai/allowance: read-only plan allowance vs credit-pack split,
 *   renewal date and history
 *
 * Rules: MS1-1, MS1-2, MS1-3, MS1-6, MS1-7, MS1-8, MS1-11; MAFS-3, MAFS-4, MAFS-5, MAFS-8.
 * Provider-neutral: contains no vendor names or vendor logic.
 * Session-based: uses session cookie via credentials: 'include'.
 * Integer-only money: amounts in integer minor units; never float math.
 * No key material: never requests, accepts, sends, or renders key material.
 */

import { ApiClientError, requestJson } from './http';

const FUNDING_PATH = '/v1/managed-ai/funding';
const BALANCE_PATH = '/v1/managed-ai/balance';
const ALLOWANCE_PATH = '/v1/managed-ai/allowance';

export type FundingSource = 'CUSTOMER_PROVIDER' | 'AUTEUR_MANAGED';

export interface ManagedRefusalReason {
  code: string;
  requirement: string;
  missing: string;
  would_require: string;
}

export interface ManagedAiFundingStatus {
  funding_source: FundingSource;
  funding_preference: FundingSource;
  available: boolean;
  refusal_code: string | null;
  refusal_reason: ManagedRefusalReason | null;
  provider_terms_status: 'allowed' | 'unconfirmed' | 'blocked' | string;
}

export interface BalanceEntry {
  type: string;
  amount: number;
  time: string;
}

export interface ManagedAiBalance {
  available: number;
  held: number;
  currency: string;
  last_updated: string | null;
  recent_entries: BalanceEntry[];
}

export type AllowanceHistoryKind =
  | 'allowance_grant'
  | 'allowance_lapse'
  | 'credit_pack'
  | 'usage'
  | 'usage_refund'
  | 'refund'
  | 'grant'
  | 'adjustment'
  | string;

export interface AllowanceHistoryEntry {
  kind: AllowanceHistoryKind;
  amount: number;
  time: string;
  expires_at: string | null;
}

/** Read-only plan allowance vs credit-pack split. Amounts are integer minor units. */
export interface ManagedAiAllowance {
  enabled: boolean;
  currency: string | null;
  available: number;
  allowance: number;
  pack: number;
  renewal_at: string | null;
  auto_top_up: boolean;
  overage_action: string;
  history: AllowanceHistoryEntry[];
}

export class ManagedAiApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ManagedAiApiError';
    this.status = status;
    this.code = code;
  }
}

export const MANAGED_AI_OPERATIONS = Object.freeze([
  'funding.read',
  'funding.update',
  'balance.read',
  'allowance.read',
]);

/**
 * Fetch tenant funding-source preference and managed availability.
 * Calls GET /v1/managed-ai/funding.
 */
export async function fetchManagedFunding(): Promise<ManagedAiFundingStatus> {
  try {
    const data = await requestJson(FUNDING_PATH, { method: 'GET' });
    return data as ManagedAiFundingStatus;
  } catch (error) {
    if (error instanceof ApiClientError) {
      throw new ManagedAiApiError(error.status, error.code, error.message);
    }
    throw error;
  }
}

/**
 * Update tenant funding-source preference.
 * Calls PUT /v1/managed-ai/funding.
 */
export async function updateFundingPreference(
  source: FundingSource,
): Promise<ManagedAiFundingStatus> {
  try {
    const data = await requestJson(FUNDING_PATH, {
      method: 'PUT',
      body: JSON.stringify({ funding_source: source }),
    });
    return data as ManagedAiFundingStatus;
  } catch (error) {
    if (error instanceof ApiClientError) {
      throw new ManagedAiApiError(error.status, error.code, error.message);
    }
    throw error;
  }
}

/**
 * Fetch tenant-scoped credit balance projection.
 * Calls GET /v1/managed-ai/balance.
 */
export async function fetchManagedBalance(): Promise<ManagedAiBalance> {
  try {
    const data = await requestJson(BALANCE_PATH, { method: 'GET' });
    return data as ManagedAiBalance;
  } catch (error) {
    if (error instanceof ApiClientError) {
      throw new ManagedAiApiError(error.status, error.code, error.message);
    }
    throw error;
  }
}

/**
 * Fetch the read-only plan allowance vs credit-pack split.
 * Calls GET /v1/managed-ai/allowance.
 */
export async function fetchManagedAllowance(): Promise<ManagedAiAllowance> {
  try {
    const data = await requestJson(ALLOWANCE_PATH, { method: 'GET' });
    return data as ManagedAiAllowance;
  } catch (error) {
    if (error instanceof ApiClientError) {
      throw new ManagedAiApiError(error.status, error.code, error.message);
    }
    throw error;
  }
}

/**
 * Determine if the managed AI feature is enabled according to the API.
 * When the master flag is off, the feature is disabled (returns false).
 */
export function isManagedAiFeatureEnabled(
  status: ManagedAiFundingStatus | null | undefined,
): boolean {
  if (!status) return false;
  if (status.available) return true;
  if (status.refusal_code === 'MANAGED_PROVIDER_OPERATION_BLOCKED') return false;
  if (status.refusal_reason?.missing?.includes('master flag is off')) return false;
  return true;
}
